import { API_BASE_URL } from "./api";

// POST to an endpoint that answers with Server-Sent Events and hand each event
// to `onEvent(name, data)`. EventSource can't be used: it only sends GET.
//
// Errors before the stream starts (400/401/403/404/429) come back as a normal
// JSON error and are thrown with `status` and the server's `message`. An
// expired session (401) is also sent to the app-wide handler (login).
// Aborting `signal` closes the connection, which stops the AI work server-side.
export async function postEventStream(endpoint, onEvent, { signal } = {}) {
  const res = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "text/event-stream" },
    signal,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message =
      (Array.isArray(body?.message) ? body.message.join("; ") : body?.message) ||
      `Request failed (${res.status})`;
    const error = new Error(message);
    error.status = res.status;
    error.endpoint = endpoint;
    if (res.status === 401) {
      window.dispatchEvent(
        new CustomEvent("app:http-error", {
          detail: { status: 401, message, endpoint },
        }),
      );
    }
    throw error;
  }

  const dispatch = (block) => {
    let name = "message";
    const data = [];
    block.split("\n").forEach((line) => {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
    });
    // Blocks with no data are comments / keep-alive heartbeats.
    if (data.length) onEvent(name, JSON.parse(data.join("\n")));
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    // Events end with a blank line; keep any partial event in the buffer.
    buffer += decoder.decode(value, { stream: true }).replace(/\r/g, "");
    let end;
    while ((end = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      if (block.trim()) dispatch(block);
    }
  }
  buffer += decoder.decode().replace(/\r/g, "");
  if (buffer.trim()) dispatch(buffer);
}
